// capabilityLoopStages.js — B7 (2026-08-18).
//
// The "Capability loop" tab in the Agent Detail Viewer presents the six-station
// lifecycle Template → Run → Evaluate → Improve → Promote → Observe as ONE
// connected journey. The stations themselves live scattered across Marketplace /
// Builder / Library / Analytics / Observability Hub. This module is the SINGLE
// source of truth for which real surface each station links to — grounded in the
// A7 guide `frontend/public/help/core-capability-loop.html` and the app's real
// routes (splat-routed via Shell.jsx: /marketplace, /builder/:id, /library/:id,
// /analytics, /analytics/observability/{datasets,evals,prompts}).
//
// LOGIC lives here (fully unit-tested); the LifecycleTab host shell is a thin
// presentational renderer over `CAPABILITY_LOOP_STAGES` + `stageActions()`.
//
// Honest framing (per the guide's Station 5): there is NO single "Promote"
// button. Promotion is four separate actions on four surfaces with four gates.
// The `promote` stage carries `honest: true` and lists all four — never a lone
// "Promote" action.

// Token substituted with the current agent id when an action targets a
// per-agent surface (e.g. "/builder/:agentId").
export const AGENT_ID_TOKEN = ":agentId";

// ── builders (statements, not object-literal property lines, so coverage
//    attributes them; the exported constant below is assembled by calling
//    these) ─────────────────────────────────────────────────────────────
function action(label, route, note) {
  const a = {};
  a.label = label;
  a.route = route;
  a.note = note || null;
  a.perAgent = typeof route === "string" && route.indexOf(AGENT_ID_TOKEN) !== -1;
  return a;
}

function stage(key, index, title, surface, description, actions, honest) {
  const s = {};
  s.key = key;
  s.index = index;
  s.title = title;
  s.surface = surface;
  s.description = description;
  s.actions = actions;
  s.honest = honest === true;
  return s;
}

// ── the six stations, in loop order ──────────────────────────────────────
function buildStages() {
  const stages = [];

  stages.push(stage(
    "template", 1, "Template", "Marketplace / Builder",
    "Everything starts from a template: install a pre-built agent from the " +
    "Marketplace, or compose one from scratch on the Builder canvas. You are " +
    "choosing the starting point, not the finished agent.",
    [
      action("Browse Marketplace", "/marketplace"),
      action("Open in Builder", "/builder/" + AGENT_ID_TOKEN),
    ],
  ));

  stages.push(stage(
    "run", 2, "Run", "Builder test panel / Library chat",
    "A defined agent does nothing until it processes a message. Send it real " +
    "input — first as a private test in the Builder panel, then as a customer " +
    "would in Library chat — and watch it route, retrieve, call tools and answer.",
    [
      action("Chat in Library", "/library/" + AGENT_ID_TOKEN),
      action("Test in Builder", "/builder/" + AGENT_ID_TOKEN),
    ],
  ));

  stages.push(stage(
    "evaluate", 3, "Evaluate", "Datasets / Evals / Inspector",
    "Running produces answers; evaluating tells you whether they were right. " +
    "Score the agent against curated question/expected-answer sets, and read the " +
    "Fact Checker's per-conversation verdict in the Inspector.",
    [
      action("Datasets", "/analytics/observability/datasets"),
      action("Evals", "/analytics/observability/evals"),
      action("Inspector", "/analytics",
        "Open a conversation from Analytics or the Activity tab to launch the Inspector."),
    ],
  ));

  stages.push(stage(
    "improve", 4, "Improve", "A/B · Learn · Fine-tune",
    "Evaluation pointed at a gap; Improve is where you build a candidate that " +
    "closes it. Three levers, lightest to heaviest, not mutually exclusive: A/B " +
    "testing, continual learning (the Learn tab), and fine-tuning.",
    [
      action("A/B testing", "/analytics"),
      action("Learn tab", "/library"),
      action("Fine-tuning", "/library"),
    ],
  ));

  // Station 5 — Promote. HONEST: no single button. Four surfaces, four gates.
  stages.push(stage(
    "promote", 5, "Promote", "Spread across four surfaces",
    "There is no single \"Promote\" button today. Promotion means \"make the " +
    "improved thing the one customers get\" — and depending on what you improved, " +
    "that action lives on a different surface. All four real paths are below.",
    [
      action("A/B test winner", "/analytics",
        "When variants reach min_conversations, the winner promotes (auto or manual)."),
      action("Fine-tune deploy gate", "/library",
        "Gated on eval score ≥ 90% AND a mandatory A/B win vs the production model."),
      action("Prompt-registry promote", "/analytics/observability/prompts",
        "Owner-gated: promote a prompt version to production in the registry."),
      action("Publish to Marketplace", "/marketplace",
        "Promotes a proven agent to other teams; installs are side-by-side, not forced upgrades."),
    ],
    true,
  ));

  stages.push(stage(
    "observe", 6, "Observe", "Analytics / Inspector",
    "The improved agent is live. Observe confirms it actually helped — and catches " +
    "regressions. This station closes the loop: a rising INACCURATE rate or cost " +
    "spike here is the input to the next turn's Template/Improve decision.",
    [
      action("Analytics dashboards", "/analytics"),
      action("Inspector", "/analytics",
        "Drill into any single conversation's stage timeline and fact-check verdict."),
    ],
  ));

  return stages;
}

export const CAPABILITY_LOOP_STAGES = buildStages();

// ── helpers (logic unit — 100% covered) ──────────────────────────────────

// Substitute the agent id into a per-agent route. Returns null when the route
// needs an agent but none is available (so the caller can drop that action
// rather than navigate to a broken "/builder/:agentId" literal).
export function resolveRoute(route, agentId) {
  if (typeof route !== "string" || route.length === 0) return null;
  if (route.indexOf(AGENT_ID_TOKEN) === -1) return route;
  if (!agentId) return null;
  return route.split(AGENT_ID_TOKEN).join(encodeURIComponent(agentId));
}

export function getStage(key) {
  for (let i = 0; i < CAPABILITY_LOOP_STAGES.length; i++) {
    if (CAPABILITY_LOOP_STAGES[i].key === key) return CAPABILITY_LOOP_STAGES[i];
  }
  return null;
}

// Resolve a stage's actions against the current agent. Per-agent actions with no
// agent id are dropped; each returned entry carries a concrete `route`.
export function stageActions(key, agentId) {
  const s = getStage(key);
  if (!s) return [];
  const out = [];
  for (let i = 0; i < s.actions.length; i++) {
    const a = s.actions[i];
    const href = resolveRoute(a.route, agentId);
    if (href === null) continue;
    out.push({ label: a.label, route: href, note: a.note, perAgent: a.perAgent });
  }
  return out;
}

// The primary (first resolvable) route for a stage, or null if none resolves.
export function stageRoute(key, agentId) {
  const acts = stageActions(key, agentId);
  return acts.length > 0 ? acts[0].route : null;
}

// True when any of a stage's actions target a per-agent surface.
export function requiresAgent(key) {
  const s = getStage(key);
  if (!s) return false;
  for (let i = 0; i < s.actions.length; i++) {
    if (s.actions[i].perAgent) return true;
  }
  return false;
}
